import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
// Import UI Components
import { Icon, Tooltip } from '@geneui/components';
// Import Components
import BlockedBySubscriptionInfo from 'components/common/BlockedBySubscriptionInfo';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import Helpers
import { getCJBlockTitleLabel } from 'services/Helpers';
// Import Constants
import { l } from 'constants/common';

const Block = ({ titleKey, type, defaultIcon, cssClassName, metaData }) => {
    const { t } = useTranslation();
    const { hasSubscriptionCJBlock } = useHasPermission();

    const isAvailable = useMemo(() => hasSubscriptionCJBlock(metaData.Name), [hasSubscriptionCJBlock, metaData.Name]);

    const blockTitleLabel = getCJBlockTitleLabel(titleKey);
    const blockDescriptionLabel = l[`CJBlock${titleKey}Description`];

    const onDragStart = (event) => {
        const blockData = JSON.stringify({
            type,
            titleKey,
            blockTitleLabel,
            defaultIcon,
            cssClassName,
            metaData,
        });
        event.dataTransfer.setData('text/plain', blockData);
        event.dataTransfer.effectAllowed = 'move';
    };

    return (
        <BlockedBySubscriptionInfo position="right" isBlocked={!isAvailable}>
            <div
                className={classNames('crm-flow-sidebar-block', { 'crm-flow-sidebar-block-disabled': !isAvailable })}
                onDragStart={(e) => isAvailable && onDragStart(e)}
                draggable={isAvailable}
            >
                <div className="crm-flow-sidebar-block-icon">
                    <Icon type={defaultIcon} />
                </div>
                <Tooltip size="default" text={t(blockTitleLabel)}>
                    <span>{t(blockTitleLabel)}</span>
                </Tooltip>
                <Tooltip title={t(blockTitleLabel)} position="bottom" text={t(blockDescriptionLabel)}>
                    <Icon type="bc-icon-info" className="crm-flow-sidebar-info-icon" />
                </Tooltip>
            </div>
        </BlockedBySubscriptionInfo>
    );
};

Block.propTypes = {
    titleKey: PropTypes.string.isRequired,
    type: PropTypes.string,
    defaultIcon: PropTypes.string,
    cssClassName: PropTypes.string,
    metaData: PropTypes.object.isRequired,
};

Block.defaultProps = {
    type: 'default',
    defaultIcon: '',
    cssClassName: '',
};

export default memo(Block);
